import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box
} from '@mui/material';
import api from '../services/api';
import { fetchCurrentUser } from '../services/auth';
import DechargeArgentPDF from './DechargeArgentPDF';
import { PDFViewer } from '@react-pdf/renderer';
import { useNavigate } from 'react-router-dom';

const DechargeArgentForm = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [submittedData, setSubmittedData] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);

  // Confirmation step states
  const [confirmationData, setConfirmationData] = useState(null);
  const [loadingConfirmation, setLoadingConfirmation] = useState(false);

  // Fetch employees
  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        try {
          const employeesResponse = await api.get('/employees');
          // Trier les employés par nom (first_name puis last_name)
          const sortedEmployees = employeesResponse.data.sort((a, b) => {
            const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
            const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
            return nameA.localeCompare(nameB);
          });
          setEmployees(sortedEmployees);
        } catch (error) {
          console.error('Erreur lors du chargement des données:', error);
        }
      };
      fetchData();
    }
  }, [open]);

  const formik = useFormik({
    initialValues: {
      employeeId: employee?.id || '', // Pre-fill with current employee ID if available
      type_decharge: 'Argent',
      reason: '',
      somme_argent: 0,
      unite_argent: 'DA',
      objet_type: '',
      objet_autre_preciser: '',
      use_cni: false,
    },
    validationSchema: Yup.lazy(values => 
    Yup.object({
      employeeId: Yup.number().required('Champ requis.'),
      type_decharge: Yup.string().required('Champ requis.'),
      reason: Yup.string().required('Champ requis.'),
      somme_argent: values.type_decharge === 'Argent' 
        ? Yup.number().required('Champ requis.')
        : Yup.number().nullable(),
      unite_argent: values.type_decharge === 'Argent' 
        ? Yup.string().required('Champ requis.')
        : Yup.string().nullable(),
      objet_type: values.type_decharge === 'Objet' 
        ? Yup.string().required('Champ requis.')
        : Yup.string().nullable(),
      objet_autre_preciser: values.objet_type === 'Autres' 
        ? Yup.string().required('Champ requis.')
        : Yup.string().nullable(),
      use_cni: Yup.boolean().required('Champ requis.'),
    })
  ),
    onSubmit: (values) => {
      setConfirmationData(values);
    },
  });

  // Handle confirmation step
  const handleConfirm = async () => {
    if (loadingConfirmation) {
      return; // Prevent multiple clicks
    }
    
    try {
      setLoadingConfirmation(true);
      const response = await api.post('/decharge-argents', confirmationData);
      if (response.status === 201) {
        const updatedData = {
          ...response.data,
          employee: employees.find((emp) => emp.id === response.data.employee_id),
        };
        setSubmittedData(updatedData);
        setIsFormDisabled(true);
        formik.resetForm();
        navigate('/decharge-argents');
      }
    } catch (error) {
      setErrorMessage("Erreur lors de la création de la décharge d'argent");
      console.error('Erreur:', error);
      
      // Handle duplicate error
      if (error.response?.status === 409) {
        setErrorMessage('Une décharge identique a déjà été créée récemment. Veuillez patienter.');
      }
    } finally {
      setLoadingConfirmation(false);
      setConfirmationData(null);
    }
  };

  const handleCancel = () => {
    setConfirmationData(null);
  };

  const handleClose = () => {
    formik.resetForm();
    setConfirmationData(null);
    setIsFormDisabled(false);
    setErrorMessage(null);
    onClose();
  };

  // Fetch user role on component mount
  useEffect(() => {
    const loadUser = async () => {
      const currentUser = await fetchCurrentUser();
      if (currentUser) {
        setUserRole(currentUser.role);
        setEmployee(currentUser.employee);
        if (currentUser.role?.toString() !== "1" && currentUser.employee?.id) {
          formik.setFieldValue('employeeId', currentUser.employee.id);
        }
      }
      setLoading(false);
    };
    loadUser();
  }, []); // Remove formik dependency to prevent infinite loop

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Nouvelle décharge</DialogTitle>
      <DialogContent>
        {errorMessage && <Typography color="error" sx={{ mb: 2 }}>{errorMessage}</Typography>}
        <Box sx={{ mt: 1 }}>
          {loadingConfirmation ? (
            <LinearProgress />
          ) : !confirmationData ? (
            <form onSubmit={formik.handleSubmit}>
              {/* Employee Selector */}
              <FormControl fullWidth margin="dense" disabled={isFormDisabled}>
                <InputLabel>Employé</InputLabel>
                <Select
                  name="employeeId"
                  value={userRole?.toString() === "1" ? formik.values.employeeId : (employee?.id || '')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.employeeId && Boolean(formik.errors.employeeId)}
                  disabled={isFormDisabled}
                >
                  {userRole?.toString() === "1" ? (
                    employees.length > 0 ? (
                      employees.map((emp) => (
                        <MenuItem key={emp.id} value={emp.id}>
                          {`${emp.first_name} ${emp.last_name}`}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>
                        Chargement des employés...
                      </MenuItem>
                    )
                  ) : (
                    <MenuItem value={employee?.id || ''}>
                      {employee ? `${employee.first_name} ${employee.last_name}` : 'Loading...'}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              {/* Type décharge */}
              <FormControl fullWidth margin="dense" disabled={isFormDisabled}>
                <InputLabel>Type décharge</InputLabel>
                <Select
                  name="type_decharge"
                  value={formik.values.type_decharge}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.type_decharge && Boolean(formik.errors.type_decharge)}
                  disabled={isFormDisabled}
                >
                  <MenuItem value="Argent">Argent</MenuItem>
                  <MenuItem value="Objet">Objet</MenuItem>
                </Select>
              </FormControl>

              {/* Motif */}
              <TextField
                fullWidth
                margin="dense"
                multiline
                rows={3}
                name="reason"
                label="Motif"
                value={formik.values.reason}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.reason && Boolean(formik.errors.reason)}
                helperText={formik.touched.reason && formik.errors.reason}
                disabled={isFormDisabled}
              />

              {/* Champs conditionnels selon le type de décharge */}
              {formik.values.type_decharge === 'Argent' ? (
                <Grid container spacing={2} alignItems="center">
                  {/* Somme */}
                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      margin="dense"
                      type="number"
                      name="somme_argent"
                      label="Somme"
                      value={parseFloat(formik.values.somme_argent || 0).toFixed(2)} // Ensures 2 decimal places
                      onChange={(e) => {
                        const value = parseFloat(e.target.value).toFixed(2);
                        formik.setFieldValue('somme_argent', isNaN(value) ? '' : value); // Handles NaN cases
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.somme_argent && Boolean(formik.errors.somme_argent)}
                      helperText={formik.touched.somme_argent && formik.errors.somme_argent}
                      disabled={isFormDisabled}
                      inputProps={{
                        step: '0.01', // Allows input in increments of 0.01
                        maxLength: 13, // Ensures the total length doesn't exceed 10 digits + 2 decimals + 1 dot
                      }}
                    />
                  </Grid>

                  {/* Monnaie */}
                  <Grid item xs={4}>
                    <FormControl fullWidth margin="dense" disabled={isFormDisabled}>
                      <InputLabel>Monnaie</InputLabel>
                      <Select
                        name="unite_argent"
                        value={formik.values.unite_argent}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.unite_argent && Boolean(formik.errors.unite_argent)}
                      >
                        <MenuItem value="DA">DA</MenuItem>
                        <MenuItem value="USD">USD</MenuItem>
                        <MenuItem value="EUR">EUR</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              ) : (
                <>
                  {/* Type d'objet */}
                  <FormControl fullWidth margin="dense" disabled={isFormDisabled}>
                    <InputLabel>Type d'objet</InputLabel>
                    <Select
                      name="objet_type"
                      value={formik.values.objet_type}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.objet_type && Boolean(formik.errors.objet_type)}
                      disabled={isFormDisabled}
                    >
                      <MenuItem value="Autres">Autres</MenuItem>
                      <MenuItem value="Telephone">Téléphone</MenuItem>
                      <MenuItem value="Véhicule">Véhicule</MenuItem>
                      <MenuItem value="chargeur">Chargeur</MenuItem>
                      <MenuItem value="clé">Clé</MenuItem>
                      <MenuItem value="ordinateur">Ordinateur</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Champ pour préciser si "Autres" */}
                  {formik.values.objet_type === 'Autres' && (
                    <TextField
                      fullWidth
                      margin="dense"
                      name="objet_autre_preciser"
                      label="Préciser l'objet"
                      value={formik.values.objet_autre_preciser}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.objet_autre_preciser && Boolean(formik.errors.objet_autre_preciser)}
                      helperText={formik.touched.objet_autre_preciser && formik.errors.objet_autre_preciser}
                      disabled={isFormDisabled}
                    />
                  )}
                </>
              )}

              {/* CNI/Griffe */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.use_cni}
                    onChange={formik.handleChange}
                    name="use_cni"
                    color="primary"
                  />
                }
                label="Utiliser CNI"
                style={{ marginLeft: 8, marginTop: 8 }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isFormDisabled}
                >
                  Créer
                </Button>
              </Box>
            </form>
          ) : (
            <Box>
              <Typography variant="h6">Confirmation de création</Typography>
              
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Employé :</Typography>
              <Typography>
                {employees.find(e => e.id === confirmationData.employeeId)?.first_name}{' '}
                {employees.find(e => e.id === confirmationData.employeeId)?.last_name}
              </Typography>
              
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Type décharge :</Typography>
              <Typography>{confirmationData.type_decharge}</Typography>
              
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Motif :</Typography>
              <Typography>{confirmationData.reason}</Typography>
              
              {confirmationData.type_decharge === 'Argent' ? (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Somme :</Typography>
                  <Typography>{confirmationData.somme_argent} {confirmationData.unite_argent}</Typography>
                </>
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Type d'objet :</Typography>
                  <Typography>
                    {confirmationData.objet_type === 'Autres' 
                      ? `Autres: ${confirmationData.objet_autre_preciser}`
                      : confirmationData.objet_type
                    }
                  </Typography>
                </>
              )}
              
              <Typography variant="subtitle2" sx={{ mt: 2 }}>CNI / Griffe :</Typography>
              <Typography>{confirmationData.use_cni ? 'CNI' : 'Griffe'}</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {!confirmationData ? (
          <Button onClick={handleClose} color="secondary">
            Annuler
          </Button>
        ) : (
          <>
            <Button onClick={handleCancel} color="secondary">
              Annuler
            </Button>
            <Button onClick={handleConfirm} color="primary" variant="contained">
              Confirmer
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DechargeArgentForm;